import { ShefErrorCode } from 'common/errors/ShefErrors';
import { ClientEvents } from 'common/events/ClientEvents';
import { isAvailableOnDate } from 'common/FoodItemAvailability';
import { StorageKeys } from 'common/storage/constants';
import { urlDateFormat } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import { compact, isNil, sum } from 'lodash';
import { DateTime } from 'luxon';
import QueryString from 'query-string';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Redirect, RouteChildrenProps } from 'react-router';
import { findShefApiResponseErrorWithCode } from 'src/error/findShefApiResponseErrorWithCode';
import {
  ShefOrderQueryQuery,
  useShefMenuSegmentsQueryQuery,
  useShefOrderQueryQuery,
} from 'src/gqlReactTypings.generated.d';
import { LoadingWithHeight } from 'src/shared/design-system/Loading';
import { GqlQueryWrapper } from 'src/shared/generic-components/GqlQueryWrapper';
import { useModal, UseModalResult } from 'src/shared/hooks/useModal';
import { useTrackOnMount } from 'src/shared/hooks/UseTrackOnMount';
import Notifications from 'src/shared/Notifications';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { dietaryTagToMenuFilter } from 'src/shared/utils/diet-utils';
import { getSlug, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { scrollWindowToTop } from 'src/shared/utils/WindowUtils';
import { useBackButton } from 'src/shef-global-state/backButtonState';
import { usePreviousPage } from 'src/util/history';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { useCachedZipCode } from '../../../user-preferences/useCachedZipCode';
import { TabNames } from './components/constants';
import { ConsumerOrder } from './components/ConsumerOrder';
import { useShefMenuDates } from './shef-menu/useShefMenuDates';
import { ZipModal } from './zip-modal/ZipModal';

interface ConsumerOrderOneTimePageProps extends RouteChildrenProps {
  data: ShefOrderQueryQuery;
  zipModal: UseModalResult;
  cachedZipCode: string | null;
}

export const ConsumerOrderOneTimePage: React.FC<ConsumerOrderOneTimePageProps> = (props) => {
  const { data, match, location, history, zipModal, cachedZipCode } = props;
  const { shef } = data;
  const { id: shefId } = shef;

  const urlParams = useMemo(() => QueryString.parse(location.search), [location.search]);

  const { deliveryDate, onDeliveryDaySelection, availabilityInfo } = useShefMenuDates(
    urlParams,
    shefId,
    zipModal,
    cachedZipCode,
    data
  );

  useTrackOnMount(ClientEvents.SHEF_MENU_VISIT, {});

  const [dietaryPrefs, setDietaryPrefs] = useLocalStorage<DietaryTags[]>(StorageKeys.DIETARY_PREFERENCES, []);
  const [consumerFoodItems, hasMoreFoodItems] = useMemo(() => {
    const foodItems = data.consumerFoodItems;

    if (dietaryPrefs.length === 0) {
      return [foodItems, false];
    }

    const filteredFoodItems = foodItems.filter((item) => {
      const dietScore = sum(dietaryPrefs.map((tag) => Number(dietaryTagToMenuFilter[tag](item) ?? false)));
      return dietScore === dietaryPrefs.length;
    });

    const hasMore = filteredFoodItems.length < foodItems.length;

    return [filteredFoodItems, hasMore];
  }, [data.consumerFoodItems, dietaryPrefs]);

  const showAllFoodItems = useCallback(() => {
    if (hasMoreFoodItems) {
      setDietaryPrefs([]);
      scrollWindowToTop();
    }
  }, [hasMoreFoodItems, setDietaryPrefs]);

  const onIncrement = useCallback(() => {
    if (!cachedZipCode) {
      zipModal.show();
    }
    return true;
  }, [cachedZipCode, zipModal]);

  const { matchesRoute, previousUrl } = usePreviousPage();
  const backPath = useMemo(() => {
    if (previousUrl && matchesRoute(Routes.CONSUMER_EXPLORE)) {
      return previousUrl;
    }
    return getSluggedPath(Routes.CONSUMER_EXPLORE, [
      { slug: Slugs.ZIP_CODE, value: cachedZipCode ?? data.shef.shefProfile.randomZip ?? '' },
    ]);
  }, [cachedZipCode, data, matchesRoute, previousUrl]);

  useBackButton(backPath);

  const filteredFoodItems = useMemo(
    () =>
      compact(
        consumerFoodItems.filter((item) => {
          if (!deliveryDate) return true;
          return item ? isAvailableOnDate(item, deliveryDate, data.shef.shefProfile.timeZone) : false;
        })
      ),
    [data, deliveryDate, consumerFoodItems]
  );

  // Fallback accounts for sold out shefs
  const nonNullDeliveryDate =
    deliveryDate ?? DateTime.fromJSDate(new Date(), { zone: data.shef.shefProfile.timeZone }).startOf('day');

  const checkoutLocation = {
    pathname: Routes.CONSUMER_ORDER_FINALIZE,
    search: QueryString.stringify({
      [QueryParams.DATE]: nonNullDeliveryDate.toFormat(urlDateFormat),
      [QueryParams.SHEF]: [shefId],
      [QueryParams.RANK]: urlParams[QueryParams.RANK],
      [QueryParams.EXPLORE_SEGMENT_ID]: urlParams[QueryParams.EXPLORE_SEGMENT_ID],
      [QueryParams.EXPLORE_SEGMENT_POS]: urlParams[QueryParams.EXPLORE_SEGMENT_POS],
      [QueryParams.EXPLORE_SEGMENT_RENDERED_ITEMS]: urlParams[QueryParams.EXPLORE_SEGMENT_RENDERED_ITEMS],
      [QueryParams.EXPLORE_SEGMENT_TOTAL_ITEMS]: urlParams[QueryParams.EXPLORE_SEGMENT_TOTAL_ITEMS],
    }),
  };

  const { data: segments, refetch } = useShefMenuSegmentsQueryQuery({
    variables: {
      shefId,
      availableDate: deliveryDate?.toJSDate().toISOString(),
    },
  });

  useEffect(() => {
    refetch({
      shefId,
      availableDate: deliveryDate?.toJSDate().toISOString(),
    });
  }, [refetch, deliveryDate, shefId]);

  return (
    <ConsumerOrder
      showDishTags={true}
      shefId={shefId}
      setDeliveryDate={onDeliveryDaySelection}
      deliveryDate={deliveryDate}
      availableFoodItems={filteredFoodItems}
      allFoodItems={consumerFoodItems}
      availabilityInfo={availabilityInfo}
      zipInfo={data.zipCode ?? undefined}
      shef={shef}
      location={location}
      match={match}
      history={history}
      nextStepLocation={checkoutLocation}
      isEditingOrder={false}
      onIncrement={onIncrement}
      activeTab={TabNames.ORDER_AHEAD}
      reviews={data.publicReviews.reviews}
      hasMoreFoodItems={hasMoreFoodItems}
      showAllFoodItems={showAllFoodItems}
      segments={segments?.shefMenuSegments ?? []}
    />
  );
};

export const ConsumerOrderOneTime: React.FC<RouteChildrenProps> = (props) => {
  const { match } = props;
  const [cachedZipCode, setCachedZipCode] = useCachedZipCode();
  const zipModal = useModal();

  const { data, error, loading } = useShefOrderQueryQuery({
    variables: {
      shefId: getSlug(match, Slugs.ID),
      zipCode: cachedZipCode,
      reviews: 3,
    },
    fetchPolicy: 'cache-and-network',
  });

  const hideZipModal = useCallback(() => {
    zipModal.hide();
  }, [zipModal]);

  useEffect(() => {
    if (error) {
      const zipCodeError = findShefApiResponseErrorWithCode(error, ShefErrorCode.INVALID_ZIP);
      // If a user has an invalid zipcode stored in local storage, this will
      // result in a GQL error. The best thing we can do at this point is clear
      // out the bad value.
      if (!isNil(zipCodeError)) {
        setCachedZipCode(null);
      }
    }
  }, [error, setCachedZipCode]);

  if (error) {
    if (
      !isNil(findShefApiResponseErrorWithCode(error, ShefErrorCode.SHEF_DNE)) ||
      !isNil(findShefApiResponseErrorWithCode(error, ShefErrorCode.SHEF_NOT_AVAILABLE))
    ) {
      Notifications.error('Sorry, this shef is not available.');
      return <Redirect to={Routes.CONSUMER_LANDING} />;
    }
  }

  if (!data) return <LoadingWithHeight delayMs={500} />;

  return (
    <>
      <GqlQueryWrapper loading={loading && !data} error={error} errorMessage='Failed to load Shef'>
        <ConsumerOrderOneTimePage {...props} data={data} zipModal={zipModal} cachedZipCode={cachedZipCode} />
      </GqlQueryWrapper>
      <ZipModal isShowing={zipModal.isShowing} onClose={hideZipModal} setZip={setCachedZipCode} />
    </>
  );
};
export default ConsumerOrderOneTime;
